import { Collaboration } from "@back4app2/sdk";
import { ReactComponent as EditPenSVG } from "../../assets/images/edit-pen-icon.svg";

/**
 * The circle with a letter in it.
 *
 * There is no display name anywhere in the API — `Collaboration` carries an
 * email and nothing else — so the initial comes off the address, and the
 * address is the row's own title rather than a subtitle under a name.
 */
const Avatar = ({ email, isOwner }: { email: string, isOwner: boolean }) => (
  <span
    aria-hidden="true"
    className={`flex-none w-8 h-8 rounded-full flex items-center justify-center text-ds-135 font-semibold ${
      isOwner ? 'bg-action text-white' : 'bg-navy-500 text-navy-100'
    }`}
  >
    {(email || '?').charAt(0).toUpperCase()}
  </span>
);

const RoleBadge = ({ children, tone = 'default' }: { children: React.ReactNode, tone?: 'default' | 'pending' }) => (
  <span
    className={`flex-none font-courrier text-ds-105 uppercase leading-none tracking-[0.08em] px-1.5 py-[4px] rounded-[4px] border ${
      tone === 'pending'
        ? 'border-state-warn/50 text-state-warn'
        : 'border-navy-400 text-navy-200'
    }`}
  >
    {children}
  </span>
);

export interface CollaboratorItemProps {
  collaborator: Collaboration;
  onRemove: (collaborator: Collaboration) => void;
  onEdit: (collaborator: Collaboration) => void;
  isAppOwner: boolean;
  currentUserEmail: string;
}

export const CollaboratorItem = ({
  collaborator,
  onRemove,
  onEdit,
  isAppOwner,
  currentUserEmail,
}: CollaboratorItemProps) => {
  const canRemove = isAppOwner || currentUserEmail === collaborator.email;

  return (
    <div className="flex items-center gap-3 py-2">
      <Avatar email={collaborator.email} isOwner={false} />
      <span className="flex-1 min-w-0 font-courrier text-ds-135 text-white truncate" title={collaborator.email}>
        {collaborator.email}
      </span>
      {/* Pending is a real state the design does not show, and it is the
          difference between "they have access" and "we sent an email". */}
      <RoleBadge tone={collaborator.isPending ? 'pending' : 'default'}>
        {collaborator.isPending ? 'invited' : 'collaborator'}
      </RoleBadge>
      {isAppOwner ? (
        <button
          type="button"
          aria-label={`Edit permissions for ${collaborator.email}`}
          className="b4-focus flex-none rounded-control p-1 text-navy-300 hover:text-white transition-colors duration-[120ms]"
          onClick={() => onEdit(collaborator)}
        >
          <EditPenSVG className="w-4 h-4" />
        </button>
      ) : null}
      {canRemove ? (
        <button
          type="button"
          className="b4-focus flex-none rounded-control text-ds-125 font-semibold text-action-soft hover:text-white transition-colors duration-[120ms]"
          onClick={() => onRemove(collaborator)}
        >
          Remove
        </button>
      ) : null}
    </div>
  );
};

export interface CollaboratorsListProps {
  collaborators: Collaboration[];
  onRemove: (collaborator: Collaboration) => void;
  onEdit: (collaborator: Collaboration) => void;
  isAppOwner: boolean;
  currentUserEmail: string;
  /** Rendered as the first row when the viewer is the owner. The API never
   * returns the owner's identity, so nobody else can be shown it. */
  ownerEmail?: string;
}

export const CollaboratorsList = ({
  collaborators,
  onRemove,
  onEdit,
  isAppOwner,
  currentUserEmail,
  ownerEmail,
}: CollaboratorsListProps) => {
  if (collaborators.length === 0 && !ownerEmail) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {ownerEmail ? (
        <div className="flex items-center gap-3 py-2">
          <Avatar email={ownerEmail} isOwner />
          <span className="flex-1 min-w-0 font-courrier text-ds-135 text-white truncate" title={ownerEmail}>
            {ownerEmail}
          </span>
          <RoleBadge>owner</RoleBadge>
        </div>
      ) : null}
      {collaborators.map((collaborator) => (
        <CollaboratorItem
          key={collaborator.id}
          collaborator={collaborator}
          onRemove={onRemove}
          onEdit={onEdit}
          isAppOwner={isAppOwner}
          currentUserEmail={currentUserEmail}
        />
      ))}
    </div>
  );
};

export default CollaboratorsList;
